import { useMemo } from "react";

import { QuizQuestionType } from "~/constants";

import { QuizQuestion } from "../quizQuestion/QuizQuestion";
import { SingleChoiceQuestion } from "../singleChoiceQuestion/SingleChoiceQuestion";
import { MultipleChoiceQuestion } from "../multipleChoiceQuestion/MultipleChoiceQuestion";
import { FillInTheBlankQuestion } from "../fillInTheBlankQuestion/FillInTheBlankQuestion";
import { OrderingQuestion } from "../orderingQuestion/OrderingQuestion";
import { MatchingQuestion } from "../matchingQuestion/MatchingQuestion";

import type { ReactNode } from "react";
import type { QuizQuestionChangeEvent } from "../../app";
import type { MatchingQuestionProps } from "../matchingQuestion/MatchingQuestion";
import type { OrderingQuestionProps } from "../orderingQuestion/OrderingQuestion";
import type { FillInTheBlankQuestionProps } from "../fillInTheBlankQuestion/FillInTheBlankQuestion";
import type { MultipleChoiceQuestionProps } from "../multipleChoiceQuestion/MultipleChoiceQuestion";
import type { SingleChoiceQuestionProps } from "../singleChoiceQuestion/SingleChoiceQuestion";
import type { QuizViewQuestion as IQuizViewQuestion } from "./QuizViewManager";

export interface QuizViewQuestionProps {
  children?: ReactNode;
  question?: IQuizViewQuestion;
  onChange?: (event: QuizQuestionChangeEvent) => void;
}

export function getComputedProps(question: IQuizViewQuestion) {
  // Converts to string if the question type expects string
  // (vs string[])
  const toExpectedValue = (x?: string | string[]) => {
    if (
      question.type === QuizQuestionType.SingleChoice ||
      question.type === QuizQuestionType.TrueFalse
    ) {
      return Array.isArray(x) ? x[0] : x;
    }

    return x;
  };

  const props: Record<string, any> = {
    answer: toExpectedValue(question.answer),
    isValid: question.isCompleted ? true : question.validity?.valid,
    options: question.options,
    showValidity: question.isCompleted ? true : question.isValidityVisible,
    value: question.isCompleted
      ? toExpectedValue(question.answer)
      : toExpectedValue(question.value),
  };

  if (question.type === QuizQuestionType.FillInBlank) {
    props.template = question.template ?? "";
  }

  if (question.type === QuizQuestionType.Matching) {
    props.items = question.items ?? [];
  }

  return props;
}

export function QuizViewQuestion({
  children,
  question,
  onChange,
}: QuizViewQuestionProps) {
  const renderedQuestionComponent = useMemo(() => {
    if (!question) return;

    const props = {
      ...getComputedProps(question),
      onChange,
    };

    switch (question.type) {
      case QuizQuestionType.MultipleChoice:
        return (
          <MultipleChoiceQuestion {...(props as MultipleChoiceQuestionProps)} />
        );
      case QuizQuestionType.FillInBlank:
        return (
          <FillInTheBlankQuestion {...(props as FillInTheBlankQuestionProps)} />
        );
      case QuizQuestionType.Ordering:
        return <OrderingQuestion {...(props as OrderingQuestionProps)} />;
      case QuizQuestionType.Matching:
        return <MatchingQuestion {...(props as MatchingQuestionProps)} />;
      default:
        return (
          <SingleChoiceQuestion {...(props as SingleChoiceQuestionProps)} />
        );
    }
  }, [onChange, question]);

  return (
    <QuizQuestion title={question?.title} description={question?.description}>
      {renderedQuestionComponent}
      {children}
    </QuizQuestion>
  );
}
